import { useState, useEffect, useMemo } from "react";
import { useProject, useInspectionAttributes, useMilestone, useLead } from "../../../../../hooks/query";
import { MILESTONE_KEY } from "../../../../../constants/milestone";
import { useQueryClient } from "@tanstack/react-query";
import {
    buildRoofMeasurementAttributes,
    buildRoofPitchAttributeFromPitchTable,
} from "../../../../../utils/eagleViewMeasurements";

export default function useEventHandler(details: any | null) {
    const projectId = details?.Id as string | undefined;
    const queryClient = useQueryClient();
    const { useProjectUpdate, useGetEagleViewReport } = useProject();
    const { useCreate: useCreateAttribute, useListByProjectId, useUpdateSingleAttribute } =
        useInspectionAttributes();
    const { useUpsertMilestone } = useMilestone();
    const { useCreateLead, useUpdateLead } = useLead();

    const updateProjectSvc = useProjectUpdate();
    const readEagleViewReportSvc = useGetEagleViewReport();
    const createAttributeSvc = useCreateAttribute();
    const listAttributesSvc = useListByProjectId();
    const updateAttributeSvc = useUpdateSingleAttribute();
    const upsertMilestoneSvc = useUpsertMilestone();
    const createLeadSvc = useCreateLead();
    const updateLeadSvc = useUpdateLead();

    const [slug, setSlug] = useState<string>(details?.Slug ?? "");
    const [projectType, setProjectType] = useState<string>(details?.ProjectType ?? "");
    const [secondarySalesmanName, setSecondarySalesmanName] = useState<string>(details?.SecondarySalesmanName ?? "");
    const [eagleViewOrderId, setEagleViewOrderId] = useState<string>(details?.EagleViewOrderId ?? "");
    const [leadSourcesId, setLeadSourcesId] = useState<string>(details?.Lead?.LeadSourcesId ?? "");

    // Milestone Dates
    const [startDate, setStartDate] = useState<string>(details?.StartDate ?? "");

    // Helper to get date from milestone
    const getMilestoneDate = (key: string) => {
        const m = details?.ProjectTimelines?.find((m: any) => m.DateType === key);
        return m?.ActualDate || m?.TargetDate || "";
    };

    const [inspectionDate, setInspectionDate] = useState<string>("");
    const [estimateDate, setEstimateDate] = useState<string>("");
    const [permitSubmissionDate, setPermitSubmissionDate] = useState<string>("");
    const [materialPurchaseDate, setMaterialPurchaseDate] = useState<string>("");
    const [jobStartDate, setJobStartDate] = useState<string>("");
    const [completionDate, setCompletionDate] = useState<string>("");
    const [estimatedEndDate, setEstimatedEndDate] = useState<string>("");

    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setSlug(details?.Slug ?? "");
        setProjectType(details?.ProjectType ?? "");
        setSecondarySalesmanName(details?.SecondarySalesmanName ?? "");
        setEagleViewOrderId(details?.EagleViewOrderId ?? "");
        setLeadSourcesId(details?.Lead?.LeadSourcesId ?? "");

        setStartDate(details?.StartDate ?? "");

        setInspectionDate(getMilestoneDate(MILESTONE_KEY.INSPECTION));
        setEstimateDate(getMilestoneDate(MILESTONE_KEY.ESTIMATE));
        setPermitSubmissionDate(getMilestoneDate(MILESTONE_KEY.PERMIT_SUBMISSION));
        setMaterialPurchaseDate(getMilestoneDate(MILESTONE_KEY.MATERIAL_PURCHASE));
        setJobStartDate(getMilestoneDate(MILESTONE_KEY.JOB_START) || details?.StartDate || "");
        setCompletionDate(getMilestoneDate(MILESTONE_KEY.COMPLETION) || details?.ActualEndDate || "");
        setEstimatedEndDate(getMilestoneDate(MILESTONE_KEY.ESTIMATED_COMPLETION));
    }, [
        details?.Slug,
        details?.ProjectType,
        details?.SecondarySalesmanName,
        details?.EagleViewOrderId,
        details?.Lead?.LeadSourcesId,
        details?.StartDate,
        details?.ProjectTimelines,
        details?.ActualEndDate,
    ]);

    const hasChanges = useMemo(() => {
        if (!details) return false;
        const normalize = (val: any) => (val === null || val === undefined ? "" : String(val));
        
        return (
            normalize(details.Slug) !== normalize(slug) ||
            normalize(details.ProjectType) !== normalize(projectType) ||
            normalize(details.SecondarySalesmanName) !== normalize(secondarySalesmanName) ||
            normalize(details.EagleViewOrderId) !== normalize(eagleViewOrderId) ||
            normalize(details.Lead?.LeadSourcesId) !== normalize(leadSourcesId) ||
            normalize(details.StartDate) !== normalize(startDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.INSPECTION)) !== normalize(inspectionDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.ESTIMATE)) !== normalize(estimateDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.PERMIT_SUBMISSION)) !== normalize(permitSubmissionDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.MATERIAL_PURCHASE)) !== normalize(materialPurchaseDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.JOB_START)) !== normalize(jobStartDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.COMPLETION)) !== normalize(completionDate) ||
            normalize(getMilestoneDate(MILESTONE_KEY.ESTIMATED_COMPLETION)) !== normalize(estimatedEndDate)
        );
    }, [
        details,
        slug,
        projectType,
        secondarySalesmanName,
        eagleViewOrderId,
        leadSourcesId,
        startDate,
        inspectionDate,
        estimateDate,
        permitSubmissionDate,
        materialPurchaseDate,
        jobStartDate,
        completionDate,
        estimatedEndDate,
    ]);

    const syncEagleViewMeasurements = async (orderId: string) => {
        const report = await readEagleViewReportSvc.mutateAsync({ ReportId: orderId });
        const measurements = report?.TotalMeasurements;

        if (!measurements) {
            throw new Error(
                "The EagleView report was found, but it contains no measurement data. Please check the Order ID.",
            );
        }

        const measurementsObj = buildRoofMeasurementAttributes(measurements);
        const pitchAttribute = buildRoofPitchAttributeFromPitchTable(report?.PitchTable);
        const attrsToSync: Record<string, string> = { ...measurementsObj };
        if (pitchAttribute) {
            attrsToSync.RoofPitch = pitchAttribute;
        }

        const existing = await listAttributesSvc.mutateAsync({
            ProjectId: projectId || "",
        });
        const existingByName = new Map<string, string>();
        for (const row of Array.isArray(existing) ? existing : []) {
            if (row?.AttributeName && row?.Id) {
                existingByName.set(row.AttributeName, row.Id);
            }
        }

        for (const [key, value] of Object.entries(attrsToSync)) {
            const existingId = existingByName.get(key);
            if (existingId) {
                await updateAttributeSvc.mutateAsync({
                    Id: existingId,
                    Value: value,
                });
            } else {
                await createAttributeSvc.mutateAsync({
                    ProjectId: projectId || "",
                    AttributeName: key,
                    Value: value,
                });
            }
        }
    };

    const [error, setError] = useState<any>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleUpdate = async () => {
        if (!projectId || !hasChanges) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            if (!details?.EagleViewOrderId && eagleViewOrderId.trim() !== "") {
                await syncEagleViewMeasurements(eagleViewOrderId);
            }

            const payload: any = {
                Id: projectId,
                UpdatedAt: new Date().toISOString(),
            };

            const normalize = (val: any) => (val === null || val === undefined ? "" : String(val));
            if (normalize(details?.Slug) !== normalize(slug)) payload.Slug = slug;
            if (normalize(details?.ProjectType) !== normalize(projectType)) payload.ProjectType = projectType;
            if (normalize(details?.SecondarySalesmanName) !== normalize(secondarySalesmanName))
                payload.SecondarySalesmanName = secondarySalesmanName;
            if (normalize(details?.EagleViewOrderId) !== normalize(eagleViewOrderId))
                payload.EagleViewOrderId = eagleViewOrderId;

            if (normalize(details?.StartDate) !== normalize(startDate)) payload.StartDate = startDate;

            // Only call update if there are changes to the project table fields
            const projectFieldsChanged = Object.keys(payload).length > 2; // More than Id and UpdatedAt
            if (projectFieldsChanged) {
                await updateProjectSvc.mutateAsync(payload);
            }

            if (normalize(details?.Lead?.LeadSourcesId) !== normalize(leadSourcesId) && leadSourcesId) {
                if (details?.Lead?.Id) {
                    await updateLeadSvc.mutateAsync({
                        Id: details.Lead.Id,
                        LeadSourcesId: leadSourcesId,
                        Name: details.Lead.Name ?? undefined,
                        ProjectId: projectId,
                    });
                } else {
                    await createLeadSvc.mutateAsync({
                        LeadSourcesId: leadSourcesId,
                        ProjectId: projectId,
                    });
                }
            }

            // Update Milestones
            const updateMilestone = async (key: string, date: string, isPlanned: boolean = false) => {
                const current = getMilestoneDate(key);
                if (normalize(current) !== normalize(date)) {
                    await upsertMilestoneSvc.mutateAsync({
                        ProjectId: projectId,
                        MilestoneKey: key,
                        PlannedDate: isPlanned ? date : null,
                        ActualDate: !isPlanned ? date : null,
                        Status: isPlanned ? "PENDING" : "COMPLETED",
                    });
                }
            };
 
            await Promise.all([
                updateMilestone(MILESTONE_KEY.INSPECTION, inspectionDate, true),
                updateMilestone(MILESTONE_KEY.ESTIMATE, estimateDate, true),
                updateMilestone(MILESTONE_KEY.PERMIT_SUBMISSION, permitSubmissionDate, true),
                updateMilestone(MILESTONE_KEY.MATERIAL_PURCHASE, materialPurchaseDate, true),
                updateMilestone(MILESTONE_KEY.JOB_START, jobStartDate, true),
                updateMilestone(MILESTONE_KEY.COMPLETION, completionDate, false),
                updateMilestone(MILESTONE_KEY.ESTIMATED_COMPLETION, estimatedEndDate, true),
            ]);

            // Invalidate queries to refresh the UI with new data
            queryClient.invalidateQueries({ queryKey: ["project", projectId] });
            queryClient.invalidateQueries({ queryKey: ["measurement", projectId] });

            setSuccess("Project details updated successfully");
        } catch (error: any) {
            setError(error);
        } finally {
            setSaving(false);
        }
    };

    return {
        slug,
        setSlug,
        projectType,
        setProjectType,
        secondarySalesmanName,
        setSecondarySalesmanName,
        eagleViewOrderId,
        setEagleViewOrderId,
        leadSourcesId,
        setLeadSourcesId,
        startDate,
        setStartDate,
        inspectionDate,
        setInspectionDate,
        estimateDate,
        setEstimateDate,
        permitSubmissionDate,
        setPermitSubmissionDate,
        materialPurchaseDate,
        setMaterialPurchaseDate,
        jobStartDate,
        setJobStartDate,
        completionDate,
        setCompletionDate,
        estimatedEndDate,
        setEstimatedEndDate,
        saving,
        error,
        success,
        hasChanges,
        handleUpdate,
    };
}
